import React from 'react';
import {FormattedMessage} from 'react-intl';
import CompleteImpulseStateIcon, {CompleteImpulseErrorStateIcon, CompleteImpulsePendingStateIcon} from './impulse-state-icon';
import {TicketingCompleteActionError, TicketingCompleteActionSuccess} from './ticketing-actions';
import {graphql, useLazyLoadQuery} from 'react-relay/hooks';
import {IMPULSE_STATUS_GENERALIZED} from '../../../constants';
import getServiceUrl from '../../../utilities/get-service-url';
import useQuery from '../../../useQuery';
import {useLocation} from 'react-router-dom';

const RAZZLE_APP_PREFIX = process.env.RAZZLE_APP_PREFIX;

const TICKETION_COMPLETE_IMPULSE_STATE_DESCRIPTION = {
    success: {
        name: 'success',
        title: <FormattedMessage defaultMessage='Thank you for your order'/>,
        message: <FormattedMessage defaultMessage='We have sent an email with your order. You can also download your ticket on this page.'/>,
        Icon: CompleteImpulseStateIcon,
        Actions: TicketingCompleteActionSuccess
    },
    pending: {
        name: 'pending',
        title: <FormattedMessage defaultMessage='Payment in progress'/>,
        message: <FormattedMessage defaultMessage='Your payment is being processed by the bank. You will receive your ticket as soon as the payment is made.'/>,
        Icon: CompleteImpulsePendingStateIcon,
        Actions: () => null
    },
    error: {
        name: 'error',
        title: <FormattedMessage defaultMessage='Payment has not been made'/>,
        message: <FormattedMessage defaultMessage='We were unable to complete the payment. Please try using a different card or contact our support.'/>,
        Icon: CompleteImpulseErrorStateIcon,
        Actions: TicketingCompleteActionError
    }
};

const ImpulseticketingCompleteQuery = graphql`
    query useTicketingCompleteImpulseByClaimQuery($claimRequest: ClaimRequestInput) {
        impulseByClaim(request: $claimRequest) {
            id
            claim
            impulseClient {
                client {
                    id
                }
            }
            impulseService {
                service {
                    id
                    description
                    slug
                    warp
                    version
                }
            }
            qrs
            status
        }
    }
`;

export const useImpulseByClaimTicketingComplete = ({state}) => {
    const {impulseId} = useQuery();
    const checkoutImpulse = React.useMemo(() => state.find(e => e.id === impulseId), [state, impulseId]);
    const {impulseByClaim} = useLazyLoadQuery(
        ImpulseticketingCompleteQuery,
        {claimRequest: {value: checkoutImpulse.claim}}
    );
    return {impulseByClaim};
}

export const useDownloadTicketingComplete = ({state}) => {
    const {impulseByClaim} = useImpulseByClaimTicketingComplete({state});
    const [showDownloadPopUp, setShowDownloadPopUp] = React.useState(false);
    const [filesToDownload, setFilesToDownload] = React.useState([]);
    const singleDownloadLinkRef = React.useRef();
    const showDownloadPopUpCallback = React.useCallback((val) => {
        setShowDownloadPopUp(val);
    }, []);
    const downloadSuccessfullImpulseFiles = React.useCallback(() => {
        const downloadFiles = [];
        impulseByClaim.qrs.forEach((endpoint, index) => {
            endpoint.forEach((qr, qrIndex) => {
                downloadFiles.push({
                    url: `${RAZZLE_APP_PREFIX}/api/impulses/impulse/download?claim=${impulseByClaim.claim}&i=${index}&j=${qrIndex}`,
                    i: index,
                    j: qrIndex
                });
            });
        });
        setFilesToDownload(downloadFiles);
        if (downloadFiles.length > 1) {
            setShowDownloadPopUp(true);
        } else if (downloadFiles.length === 1) {
            downloadFiles.forEach((file, index) => {
                singleDownloadLinkRef.current.href = file.url;
                singleDownloadLinkRef.current.setAttribute('target', '_blank');
                singleDownloadLinkRef.current.click();
            });
        }
    });
    return {
        downloadSuccessfullImpulseFiles,
        singleDownloadLinkRef,
        showDownloadPopUp,
        setShowDownloadPopUp: showDownloadPopUpCallback,
        filesToDownload,
    }
};

const useTicketingComplete = ({state, description = TICKETION_COMPLETE_IMPULSE_STATE_DESCRIPTION}) => {
    const {impulseByClaim} = useImpulseByClaimTicketingComplete({state});
    const location = useLocation();
    const [impulseStateDescription, setImpulseStateDescription] = React.useState(description[IMPULSE_STATUS_GENERALIZED[impulseByClaim.status]]);
    const setImpulseStateDescriptionCallback = React.useCallback((state) => {
        setImpulseStateDescription(description[state]);
    }, [setImpulseStateDescription]);
    const impulseByAuthorizedUser = impulseByClaim.impulseClient.client && impulseByClaim.impulseClient.client.id;
    const serviceLink = getServiceUrl({description: impulseByClaim.impulseService.service.description, id: impulseByClaim.impulseService.service.id, slug: impulseByClaim.impulseService.service.slug, warp: impulseByClaim.impulseService.service.warp, location});
    
    return {
        serviceLink,
        impulseByAuthorizedUser,
        impulseStateDescription,
        setImpulseStateDescription: setImpulseStateDescriptionCallback,
    }
};

export default useTicketingComplete;